import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Package, ChevronRight, ChevronDown, AlertCircle } from 'lucide-react';

const VueAtelier = ({ ateliers = [], emplacements = [], outils = [], onRefresh }) => {
  const [atelierSelectionne, setAtelierSelectionne] = useState(ateliers[0]?.id || null);
  const [emplacementExpanded, setEmplacementExpanded] = useState({});

  const toggleEmplacement = (id) => {
    setEmplacementExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Protection si pas de données
  if (!ateliers || ateliers.length === 0) {
    return (
      <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl p-12 text-center">
        <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-400 text-lg">Aucun atelier configuré</p>
        <p className="text-gray-500 text-sm mt-2">Les ateliers seront créés automatiquement lors de l'installation</p>
      </div>
    );
  }

  const atelierActuel = ateliers.find(a => a.id === atelierSelectionne);
  const emplacementsAtelier = (emplacements || []).filter(e => e.atelier_id === atelierSelectionne);

  return (
    <motion.div
      key="atelier"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Sélection atelier */}
      <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Sélectionner un atelier</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {(ateliers || []).map(atelier => {
            const nbOutils = (outils || []).filter(o => {
              const emp = (emplacements || []).find(e => e.id === o.emplacement_id);
              return emp?.atelier_id === atelier.id;
            }).length;

            return (
              <button
                key={atelier.id}
                onClick={() => setAtelierSelectionne(atelier.id)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  atelierSelectionne === atelier.id
                    ? 'border-blue-500 bg-blue-600/20'
                    : 'border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className="text-center">
                  <p className="text-2xl font-bold text-white mb-1">{atelier.code}</p>
                  <p className="text-gray-300 text-sm mb-2">{atelier.nom}</p>
                  <div className="text-blue-400 font-semibold">{nbOutils} outils</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Vue de l'atelier sélectionné */}
      {atelierActuel && (
        <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-6 h-6 text-blue-500" />
            <h2 className="text-2xl font-bold text-white">{atelierActuel.nom}</h2>
          </div>

          {emplacementsAtelier.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">Aucun emplacement dans cet atelier</p>
              <p className="text-gray-500 text-sm mt-2">
                Créez des emplacements dans l'onglet "Emplacements"
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {emplacementsAtelier.map(emplacement => {
                const outilsEmplacement = (outils || []).filter(o => o.emplacement_id === emplacement.id);
                const isExpanded = emplacementExpanded[emplacement.id];

                return (
                  <div key={emplacement.id} className="bg-slate-700/50 rounded-lg overflow-hidden">
                    {/* Header emplacement */}
                    <button
                      onClick={() => toggleEmplacement(emplacement.id)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5 text-blue-400" />
                        <div className="text-left">
                          <h3 className="text-white font-semibold">{emplacement.nom}</h3>
                          <p className="text-sm text-gray-400">
                            {emplacement.type_emplacement} • {outilsEmplacement.length} outil(s)
                          </p>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </button>

                    {/* Liste des outils */}
                    {isExpanded && (
                      <div className="p-4 pt-0 space-y-2">
                        {outilsEmplacement.length === 0 ? (
                          <p className="text-gray-400 text-sm text-center py-4">
                            Aucun outil dans cet emplacement
                          </p>
                        ) : (
                          outilsEmplacement.map(outil => (
                            <div
                              key={outil.id}
                              className="flex items-center gap-3 p-3 bg-slate-600/50 rounded-lg"
                            >
                              {outil.photo_url ? (
                                <img
                                  src={outil.photo_url}
                                  alt={outil.nom}
                                  className="w-12 h-12 rounded object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded bg-slate-700 flex items-center justify-center">
                                  <Package className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="text-white font-medium">{outil.nom}</p>
                                <p className="text-sm text-gray-400">{outil.reference}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-white">Qté: {outil.quantite}</p>
                                <span
                                  className={`text-xs px-2 py-1 rounded ${
                                    outil.disponible
                                      ? 'bg-green-500/20 text-green-400'
                                      : 'bg-red-500/20 text-red-400'
                                  }`}
                                >
                                  {outil.disponible ? 'Disponible' : 'Emprunté'}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default VueAtelier;